import {
    ApiBaseService,
    type EnhancedFetch,
    type EnhancedFetchOptions,
    type PaginatedFetch,
    type PaginatedFetchOptions,
} from "./ApiBaseService";
import type { ApiResponse } from "~/types/ApiResponse";
import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import { faStar, faTrash } from "@fortawesome/pro-solid-svg-icons";
import type { Location } from "~/services/LocationService";
import { faMapLocation, faShare } from "@fortawesome/pro-light-svg-icons";
import { useClipboard } from "#imports";
import type { Onboarding } from "~/types/Onboarding";
import type { Domain } from "./DomainService";

export interface BrandThemeColor {
    color: string | null;
    text_color: string | null;
    inverse_color: string | null;
    inverse_text_color: string | null;
}

export interface BrandThemePalette {
    primary?: BrandThemeColor;
    secondary?: BrandThemeColor;
    accent?: BrandThemeColor;
    background?: BrandThemeColor;
    surface?: BrandThemeColor;
}

export interface BrandTypographyItem {
    fontFamily: string | null;
    fontWeight: number | null;
}

export interface BrandTypography {
    default?: BrandTypographyItem;
    headings?: BrandTypographyItem;
}
export interface BrandTheme {
    id: number;
    typography: BrandTypography;
    palette: BrandThemePalette;
    default?: boolean;
}

export interface BrandSocials {
    facebook: string | null;
    twitter: string | null;
    instagram: string | null;
    linked_in: string | null;
    youtube: string | null;
    tiktok: string | null;
    reddit: string | null;
    vimeo: string | null;
    git_hub: string | null;
    git_lab: string | null;
    bit_bucket: string | null;
    medium: string | null;
    twitch: string | null;
    discord: string | null;
    glassdoor: string | null;
    angel_list: string | null;
    crunchbase: string | null;
    yelp: string | null;
    trustpilot: string | null;
    indeed: string | null;
    clutch: string | null;
    g2: string | null;
    weibo: string | null;
    telegram: string | null;
    slack: string | null;
}

export interface Brand {
    id: number;
    name: string;
    avatar: string;
    banner: string;
    email_banner: string;
    email_logo: string;
    theme: BrandTheme;
    location?: Location;
    domain: Domain;
    metadata: {
        website: string | null;
        about_us: string | null;
        year_founded: string | null;
        industry: string | null;
        slogan: string | null;
        mission: string | null;
        vision: string | null;
        values: string | null;
        phone: string | null;
        company_size: string | null;
        specialties: string | null;
        socials: BrandSocials;
    };
    domain?: Domain;
    created_at: string;
    updated_at: string;
    value_map?: Record<string, any>;
    onboarding: Onboarding;
}

export default class BrandService extends ApiBaseService {
    public list(
        options?: Partial<PaginatedFetchOptions>,
    ): PaginatedFetch<Brand[]> {
        return this.paginate<Brand[]>(
            this.route("api.brands.index", "/brands"),
            options as PaginatedFetchOptions,
        );
    }

    public find(
        id: number,
        options?: EnhancedFetchOptions,
    ): EnhancedFetch<Brand> {
        return this.enhancedFetch<Brand>(
            this.route("api.brands.show", "/brands/{brand}", {
                brand: id,
            }),
            options,
        );
    }

    show(brand_id: number): Promise<ApiResponse<Brand>> {
        return this.request<ApiResponse<Brand>>(
            this.route("api.brands.show", "/brands/{brand}", {
                brand: brand_id,
            }),
            {
                method: "GET",
            },
        );
    }

    public all(options?: EnhancedFetchOptions): EnhancedFetch<Brand[]> {
        return this.enhancedFetch<Brand[]>(
            this.route("api.brands.all", "/brands/all"),
            options,
        );
    }

    public update(
        id: number,
        data: Partial<Brand>,
    ): Promise<ApiResponse<Brand>> {
        return this.request<ApiResponse<Brand>>(
            this.route("api.brands.update", "/brands/{brand}", {
                brand: id,
            }),
            {
                method: "POST",
                body: jsonToFormData(data),
                query: {
                    _method: "PATCH",
                },
            },
        ).then(async (brand) => {
            const { user, refreshIdentity } = useAuth();

            if (brand.data.id === user.value?.account?.brand?.id) {
                await refreshIdentity();
            }

            return brand;
        });
    }

    public delete(id: number): Promise<null> {
        return this.request(
            this.route("api.brands.destroy", "/brands/{brand}", {
                brand: id,
            }),
            {
                method: "DELETE",
            },
        );
    }

    public async create(
        value: Partial<{
            name: string;
            brand_fetch_id: string;
        }>,
    ): Promise<ApiResponse<Brand>> {
        return this.request(this.route("api.brands.store", "/brands"), {
            method: "POST",
            body: value,
        });
    }

    public edit(brand: Brand) {
        useBrand().value = brand;

        navigateTo({
            name: "settings-account-brands-id",
            params: {
                id: brand.id,
            },
        });
    }

    public dropdown(brand: Brand, options: { onDelete?: () => void } = {}) {
        const lists: Array<Array<Record<string, any>>> = [
            [
                {
                    label: "Edit",
                    icon: faEdit,
                    click: () => this.edit(brand),
                },
            ],
        ];

        const { user } = useUser();
        const api = useApi();

        if (
            brand.location &&
            brand.location.metadata?.google_places_details?.maps_uri
        ) {
            lists[0].splice(0, 0, {
                label: "Google Maps",
                icon: faMapLocation,
                click: () =>
                    window.open(
                        (brand.location as Location).metadata
                            ?.google_places_details?.maps_uri,
                        "_blank",
                    ),
            });
        }

        if (brand.location && brand.location.google_review_url) {
            lists[0].splice(0, 0, {
                label: "Share Google Review URL",
                icon: faShare,
                click: () => {
                    const { copy } = useClipboard({
                        source: (brand.location as Location)
                            .google_review_url as string,
                        legacy: true,
                    });

                    copy(
                        (brand.location as Location)
                            .google_review_url as string,
                    );

                    useToast().add({
                        title: "Review URL Copied",
                        actions: [
                            {
                                label: "Open URL",
                                click: () =>
                                    window.open(
                                        (brand.location as Location)
                                            .google_review_url as string,
                                        "_blank",
                                    ),
                            },
                        ],
                    });
                },
            });
        }

        if (user.value?.account?.brand?.id !== brand.id) {
            const setDefaultBrand = (id: number, original_id?: number) => {
                api.account
                    .update({
                        brand_id: id,
                    })
                    .then(async () => {
                        useToast().add(
                            original_id !== undefined
                                ? {
                                      title: "Default Brand Updated",
                                      description: `${brand.name} is now your default brand.`,
                                      actions: [
                                          {
                                              label: "Undo",
                                              click: () => {
                                                  setDefaultBrand(
                                                      original_id as number,
                                                  );
                                              },
                                          },
                                      ],
                                  }
                                : {
                                      title: "Default Brand Restored",
                                  },
                        );

                        await useAuth().refreshIdentity();
                    });
            };

            lists[0].push({
                label: "Set Default",
                icon: faStar,
                click: () =>
                    setDefaultBrand(
                        brand.id,
                        (user.value?.account?.brand?.id as number) ?? null,
                    ),
            });
        }

        if (options.onDelete) {
            lists.push([
                {
                    label: "Delete",
                    icon: faTrash,
                    class: "text-red-500",
                    click: () => (options.onDelete as () => void)(),
                },
            ]);
        }

        return lists;
    }

    public onboarding(
        brand: Brand,
        options: {
            onBrandLogoClick?: (completed: boolean) => void;
            onBrandEmailLogoClick?: (completed: boolean) => void;
            onBrandColorsClick?: (completed: boolean) => void;
            onBrandFontClick?: (completed: boolean) => void;
            onBrandWebsiteClick?: (completed: boolean) => void;
            onBrandSocialsClick?: (completed: boolean) => void;
            onBrandLocationClick?: (completed: boolean) => void;
        } = {},
    ) {
        if (!brand) return [];

        const base = brand.onboarding ?? [];

        const items = [
            {
                tag: "brand_logo",
                completed: false,
                onClick: options?.onBrandLogoClick,
            },
            {
                tag: "brand_email_logo",
                completed: false,
                onClick: options?.onBrandEmailLogoClick,
            },
            {
                tag: "brand_colors",
                completed: false,
                onClick: options?.onBrandColorsClick,
            },
            {
                tag: "brand_font",
                completed: false,
                onClick: options?.onBrandFontClick,
            },
            {
                tag: "brand_website",
                completed: false,
                onClick: options?.onBrandWebsiteClick,
            },
            {
                tag: "brand_socials",
                completed: false,
                onClick: options?.onBrandSocialsClick,
            },
            {
                tag: "brand_location",
                completed: false,
                onClick: options?.onBrandLocationClick,
            },
        ];

        return items.map((item) => {
            const baseItem = base.find((i) => i.tag === item.tag);

            return {
                ...baseItem,
                ...item,
                completed: baseItem?.completed ?? false,
            };
        });
    }
}
